/**
 * Manual code entry. Shown when the URL has no `:code` parameter
 * (someone typed the URL or shared it without the code). Live-formats
 * the input as the user types and runs the client-side checksum check
 * BEFORE letting them submit — saves a round-trip on every typo.
 */
import React, { useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { formatCodeForInput, validateCodeChecksum } from '../utils/checksum';
import { brand } from '../../../config/brand';

interface CodeEntryStepProps {
  initialValue?: string;
  /** Called with the canonical LXIA-XXXX-XXXX-XXXX form once the checksum passes. */
  onSubmit: (normalizedCode: string) => void;
  /** When true, disable the input + button while a network request is in flight. */
  disabled?: boolean;
}

const CodeEntryStep: React.FC<CodeEntryStepProps> = ({
  initialValue = '',
  onSubmit,
  disabled = false,
}) => {
  const [value, setValue] = useState(formatCodeForInput(initialValue));
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(formatCodeForInput(e.target.value));
    // Clear stale error on every keystroke — let the user fix it without
    // a red wall yelling at them mid-correction.
    if (error) setError(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const check = validateCodeChecksum(value);
    if (!check.valid) {
      // We deliberately don't surface the precise failure reason — the
      // user doesn't care if it's a charset error or a checksum miss,
      // they care that they probably typed it wrong.
      setError("This doesn't look like a valid code. Double-check and try again.");
      return;
    }
    onSubmit(check.normalized!);
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      data-testid="code-entry-step"
      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      <Typography variant="h5" component="h1" gutterBottom>
        Redeem your {brand.name} code
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Enter the 14-character code printed on your convention card.
      </Typography>
      <TextField
        autoFocus
        fullWidth
        label="Coupon code"
        placeholder="LXIA-XXXX-XXXX-XXXX"
        value={value}
        onChange={handleChange}
        disabled={disabled}
        error={!!error}
        helperText={error || ' '}
        inputProps={{
          'data-testid': 'code-entry-input',
          autoCapitalize: 'characters',
          autoCorrect: 'off',
          spellCheck: 'false',
          // Hint mobile keyboards to use the alphanumeric layout.
          inputMode: 'text',
          maxLength: 19, // LXIA-XXXX-XXXX-XXXX (4 + 4*3 + 3 dashes)
        }}
      />
      <Button
        type="submit"
        variant="contained"
        size="large"
        disabled={disabled || value.length === 0}
        data-testid="code-entry-submit"
      >
        Continue
      </Button>
    </Box>
  );
};

export default CodeEntryStep;
